import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Star, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { API } from '../../utils/constants';

/**
 * Public feedback submission page for customers
 * Accessible via /feedback/{orderId} - no authentication required
 */
function FeedbackPage({ orderId }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState(null);
  const [existingFeedback, setExistingFeedback] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  // Feedback form state
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comments, setComments] = useState('');

  // Fetch order details and existing feedback
  useEffect(() => {
    const fetchOrderData = async () => {
      try {
        setLoading(true);
        
        // Fetch order details
        const orderResp = await axios.get(`${API}/orders/${orderId}`);
        setOrder(orderResp.data);
        
        // Check for existing feedback
        const feedbackResp = await axios.get(`${API}/orders/${orderId}/feedback`);
        if (feedbackResp.data?.feedback?.rating > 0) {
          setExistingFeedback(feedbackResp.data.feedback);
          setSubmitted(true);
        }
      } catch (err) {
        console.error('Failed to fetch order:', err);
        if (err.response?.status === 404) {
          setError('Order not found. Please check your feedback link.');
        } else {
          setError('Unable to load order details. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchOrderData();
    }
  }, [orderId]);

  // Submit feedback
  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API}/orders/${orderId}/feedback`, {
        rating,
        comments: comments.trim()
      });
      
      setSubmitted(true);
      setExistingFeedback({ rating, comments: comments.trim() });
      toast.success('Thank you for your feedback!');
    } catch (err) {
      console.error('Failed to submit feedback:', err);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Render star rating input
  const renderStarInput = () => (
    <div className="flex gap-2 justify-center">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => setRating(star)}
          onMouseEnter={() => setHoverRating(star)}
          onMouseLeave={() => setHoverRating(0)}
          className="p-1 transition-transform hover:scale-110 focus:outline-none"
          data-testid={`star-${star}`}
        >
          <Star
            className={`w-10 h-10 ${
              star <= (hoverRating || rating)
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );

  // Render static star display
  const renderStarDisplay = (value) => (
    <div className="flex gap-1 justify-center">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-8 h-8 ${
            star <= value ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
          }`}
        />
      ))}
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center">
        <Toaster position="top-center" richColors />
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
        <Toaster position="top-center" richColors />
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Oops!</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Already submitted state
  if (submitted && existingFeedback) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
        <Toaster position="top-center" richColors />
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Thank You!</h1>
          <p className="text-gray-600 mb-6">
            Your feedback has been submitted successfully.
          </p>
          
          <div className="bg-gray-50 rounded-xl p-6">
            <p className="text-sm text-gray-500 mb-2">Your Rating</p>
            {renderStarDisplay(existingFeedback.rating)}
            {existingFeedback.comments && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-gray-500 mb-1">Your Comments</p>
                <p className="text-gray-700 italic">&ldquo;{existingFeedback.comments}&rdquo;</p>
              </div>
            )}
          </div>
          
          <p className="mt-6 text-sm text-gray-500">
            We appreciate you taking the time to share your experience!
          </p>
        </div>
      </div>
    );
  }

  // Feedback form
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
      <Toaster position="top-center" richColors />
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            How was your experience?
          </h1>
          <p className="text-gray-600">
            We&apos;d love to hear your feedback about your recent order
          </p>
        </div>

        {/* Order Summary */}
        {order && (
          <div className="bg-orange-50 rounded-xl p-4 mb-8">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-semibold text-gray-800">
                  {order.customer_info?.name || 'Customer'}
                </p>
                <p className="text-sm text-gray-600">
                  {order.customer_info?.occasion || order.catering_type || 'Event'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">
                  {order.customer_info?.event_date || order.customer_info?.start_date || ''}
                </p>
                <p className="text-sm font-medium text-orange-600">
                  ${Math.floor(order.total_amount || 0)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Rating Section */}
        <div className="mb-8">
          <p className="text-center text-gray-700 font-medium mb-4">
            Rate your experience
          </p>
          {renderStarInput()}
          <p className="text-center text-sm text-gray-500 mt-2">
            {rating === 0 && 'Tap a star to rate'}
            {rating === 1 && 'Poor'}
            {rating === 2 && 'Fair'}
            {rating === 3 && 'Good'}
            {rating === 4 && 'Very Good'}
            {rating === 5 && 'Excellent!'}
          </p>
        </div>

        {/* Comments Section */}
        <div className="mb-8">
          <label className="block text-gray-700 font-medium mb-2">
            Tell us more (optional)
          </label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="What did you enjoy? Any suggestions for improvement?"
            className="w-full rounded-xl border border-gray-200 p-4 h-32 resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            data-testid="feedback-comments"
          />
        </div>

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          disabled={submitting || rating === 0}
          className="w-full h-12 text-lg bg-orange-500 hover:bg-orange-600"
          data-testid="submit-feedback-btn"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            'Submit Feedback'
          )}
        </Button>

        <p className="text-center text-xs text-gray-400 mt-4">
          Your feedback helps us improve our service
        </p>
      </div>
    </div>
  );
}

export default FeedbackPage;
